<script setup>
import { ref, computed, reactive, toRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { X as XIcon } from 'lucide-vue-next';
import { DialogRoot, DialogPortal } from 'reka-ui';
import { DialogContent, DialogOverlay, DialogTitle } from 'Components/primitives/dialog';
import { useDialogStyles } from 'Components/base/EnDialog/useDialogStyles';
import { cn } from 'Components/primitives/utils';

import {useScreenSize} from 'Js/mixins/screenSizeComposable';

const {t} = useI18n();

/**
 * A dialog with a title, scrollable content and an actions row.
 * Supposed to replace generic EnDialog at a later time.
 */
const props = defineProps({
  /**
   * The text displayed as the header of the dialog.
   */
  title: String,
  /**
   * Specifies whether the small style is applied (true) or not (false).
   */
  small: {
    type: Boolean,
    default: false
  },
  /**
   * Specifies whether the dialog fits the content (true) or not (false).
   */
  autoWidth: Boolean,
  /**
   * Specifies whether the dialog actions are fixed (true) or not (false).
   */
  fixedActions: Boolean,
  /**
   * Specifies whether the close icon is shown (true) or not (false).
   * Defaults to true.
   */
  closeIcon: {
    type: Boolean,
    default: true,
  },
  /**
   * If passed this method is called before the dialog is closed.
   * If true is returned the dialog is closed.
   * If false is returned the dialog is not closed.
   */
  confirmClose: Function,
  /**
   * Specifies whether clicking outside of the element or pressing esc key will not deactivate it.
   * Defaults to true.
   */
  persistent: {
    type: Boolean,
    default: true,
  },
  height: {
    type: String,
    default: null,
  },
  width: {
    type: String,
    default: null,
  },
});

const modelValue = defineModel('modelValue');
const emit = defineEmits(['input', 'close', 'update:modelValue']);
const {
  isDesktop,
  isMobile
} = useScreenSize();

const content = ref(modelValue.value);

const maxSize = computed(() => {
  if (isDesktop) {
    return props.autoWidth === true ? '100%' : props.small ? '50%' : '80%';
  }

  return '100%';
});

const computedHeight = computed(() => {
  if (props.height) return props.height;
  return 'auto';
});

const minWidth = computed(() => {
  return isMobile ? '300px' : 'auto';
});

const minHeight = computed(() => {
  return isMobile ? '150px' : 'auto';
});


const maxWidthClass = computed(() => {
  if (isDesktop) {
    return props.autoWidth === true ? 'max-w-full' : props.small ? 'max-w-[50%]' : 'max-w-[80%]';
  }

  return 'max-w-full';
});
const { dialogStyle, contentClass } = useDialogStyles(reactive({
  autoWidth: toRef(props, 'autoWidth'),
  small: toRef(props, 'small'),
  width: toRef(props, 'width'),
  height: computedHeight,
  maxHeight: maxSize,
  minWidth,
  minHeight,
}));

function onDismiss(event) {
  if (props.persistent) event.preventDefault();
}

function close() {
  if (props.confirmClose) {
    if (!props.confirmClose()) return;
  }

  content.value = false;
  /**
   * Triggers when the dialog is closed.
   */
  emit('close');
}

watch(modelValue, async (newValue) => {
  content.value = newValue;
});
watch(content, async (newValue) => {
  if (!newValue) {
    close();
  }
  /**
   * Triggers when the value is changed.
   */
  emit('input', newValue);
  emit('update:modelValue', newValue);
});

</script>

<template>
  <dialog-root :open="content" @update:open="content = $event">
    <dialog-portal>
      <dialog-overlay></dialog-overlay>
      <dialog-content
          :class="cn('dialog wrapper p-3 flex flex-col gap-0', contentClass, maxWidthClass, fixedActions && 'no-scrollable-dialog')"
          :style="dialogStyle"
          :aria-describedby="undefined"
          @pointer-down-outside="onDismiss"
          @escape-key-down="onDismiss"
      >
        <dialog-title v-if="title" class="title">
          <div class="flex flex-wrap flex-auto justify-space-between">
            <div class="w-full basis-0 grow max-w-full">{{ title }}</div>
            <div class="w-full basis-0 grow max-w-full shrink d-flex justify-end" v-if="closeIcon">
              <x-icon class="size-6 text-primary cursor-pointer" @click="close"></x-icon>
            </div>
          </div>
        </dialog-title>

        <div class="content" :class="['text', { 'scrollable-content': fixedActions, 'mt-3': small }]">
          <!-- @slot The dialog's content. -->
          <slot name="content"></slot>
        </div>

        <div class="actions">
          <!-- @slot The dialog's actions. -->
          <slot name="actions"></slot>
        </div>

        <!-- Close Button Slot -->
        <div class="close-button-container">
          <slot name="close-button"></slot>
        </div>
      </dialog-content>
    </dialog-portal>
  </dialog-root>
</template>

<style lang="scss" scoped>
@use "Styles/helper/variables.scss";

.dialog {
  z-index: 1030;
}

.no-scrollable-dialog {
  overflow-y: initial !important;
}

.scrollable-content {
  overflow-y: auto;
  height: 66vh;
}

.title {
  margin-top: 0.5rem;
  padding: 0 1rem;
  font-size: 1.75rem !important;
  color: variables.$en-blue;
}

.text {
  color: variables.$en-100 !important;
}

.wrapper {
  position: relative;
  border: 1px solid variables.$en-30;
  border-radius: 4px;
}

.content {
  padding: 1.5rem 1.5rem;
  font-size: 1.25rem !important;
  color: variables.$en-blue-3 !important;
}

.close-button-container {
  display: flex;
  justify-content: flex-end;
  padding: 0 1.5rem .5rem;
}

</style>